'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { getSupabaseBrowserClientSafe } from '../lib/supabase/client';

type RealtimePayload = {
  new?: Record<string, unknown> | null;
  old?: Record<string, unknown> | null;
  eventType?: string;
};

export type RealtimeRefreshSubscription = {
  table: string;
  filter?: string;
  debounceMs?: number;
  matches?: (payload: RealtimePayload) => boolean;
};

export function useRealtimeRefresh(
  subscriptions: RealtimeRefreshSubscription[],
  options?: { enabled?: boolean; defaultDebounceMs?: number },
) {
  const router = useRouter();
  const client = useMemo(() => getSupabaseBrowserClientSafe(), []);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enabled = options?.enabled ?? true;
  const defaultDebounceMs = options?.defaultDebounceMs ?? 180;

  useEffect(() => {
    if (!enabled || !client || !subscriptions.length) return;

    const validSubscriptions = subscriptions.filter((item) => item.table.trim().length > 0);
    if (!validSubscriptions.length) return;

    const channelName = `refresh-${validSubscriptions
      .map((item) => `${item.table}-${item.filter || 'all'}`)
      .join('-')
      .slice(0, 120)}`;

    const scheduleRefresh = (debounceMs?: number) => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }

      refreshTimerRef.current = setTimeout(() => {
        router.refresh();
      }, debounceMs ?? defaultDebounceMs);
    };

    const channel = client.channel(channelName);

    validSubscriptions.forEach((subscription) => {
      channel.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: subscription.table,
          filter: subscription.filter,
        },
        (payload) => {
          const normalizedPayload = payload as RealtimePayload;
          if (subscription.matches && !subscription.matches(normalizedPayload)) return;
          scheduleRefresh(subscription.debounceMs);
        },
      );
    });

    channel.subscribe();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      void client.removeChannel(channel);
    };
  }, [client, defaultDebounceMs, enabled, router, subscriptions]);
}
